import React from 'react';
import { HorizontalContainer, VerticalContainer } from '@shared/designSystem/Containers';
import { Text } from '@shared/designSystem/Texts';
import designSystemStyles from '@shared/styles/designSystemStyles';
import { QontyLoading } from '@components/QontyLoading';
import { useKioskEvidenceModalViewModel } from './KioskEvidenceModalViewModel';

interface EvidencePhotoProps {
    title: string;
    caption: string;
    url?: string;
    emptyMessage: string;
    onError: () => void;
}

/**
 * One of the two photos. Renders its own empty state rather than letting a broken
 * image show: an absent photo is a normal outcome here (never enrolled, never
 * uploaded, or expired by the 90-day rule), not a failure of the modal.
 */
const EvidencePhoto = ({ title, caption, url, emptyMessage, onError }: EvidencePhotoProps) => (
    <VerticalContainer gap={designSystemStyles.gapSm} alignment="left" width="100%">
        <Text text={`**${title}**`} markdown={true} />
        <div
            style={{
                width: '100%',
                aspectRatio: '3 / 4',
                borderRadius: designSystemStyles.cornerRadius,
                border: `1px solid ${designSystemStyles.colorBorderSubtle}`,
                background: designSystemStyles.colorBackgroundGray2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden'
            }}
        >
            {url ? (
                <img
                    src={url}
                    alt={title}
                    onError={onError}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
            ) : (
                <div style={{ padding: designSystemStyles.gapLg, textAlign: 'center' }}>
                    <Text text={emptyMessage} />
                </div>
            )}
        </div>
        <Text text={caption} />
    </VerticalContainer>
);

interface KioskEvidencePhotosProps {
    /** the presenteeism record of the kiosk fichada */
    recordID: string;
}

/**
 * The collaborator's reference photo next to the frame the kiosk captured, loaded
 * from the kiosk-record evidence endpoint. Used by the fichada evidence modal and by
 * the kiosk spoof alert detail.
 */
const KioskEvidencePhotos = ({ recordID }: KioskEvidencePhotosProps) => {
    const {
        loading,
        error,
        evidence,
        evidenceImageURL,
        referenceImageURL,
        onEvidenceImageError,
        onReferenceImageError
    } = useKioskEvidenceModalViewModel({ recordID });

    if (loading) return <QontyLoading />;
    if (error) return <Text text={error} />;
    if (!evidence) return null;

    return (
        <VerticalContainer gap={designSystemStyles.gapMd} alignment="left" width="100%">
            <HorizontalContainer gap={designSystemStyles.gapLg} width="100%" alignment="flex-start">
                <EvidencePhoto
                    title={"Foto registrada".localized()}
                    caption={"Foto de referencia del colaborador".localized()}
                    url={referenceImageURL}
                    emptyMessage={"Sin foto de referencia".localized()}
                    onError={onReferenceImageError}
                />
                <EvidencePhoto
                    title={"Foto de la fichada".localized()}
                    caption={"Capturada por el kiosco al fichar".localized()}
                    url={evidenceImageURL}
                    emptyMessage={"Sin foto de la fichada".localized()}
                    onError={onEvidenceImageError}
                />
            </HorizontalContainer>
            <Text
                text={"La foto de referencia es la que el colaborador tiene registrada hoy, que puede no ser la misma que se usó al fichar.".localized()}
            />
        </VerticalContainer>
    );
};

export default KioskEvidencePhotos;
